import { useState, useEffect, useCallback, useRef } from "react";
import { getJusGPTUsage, invalidateJusGPTUsage } from "../api";
import {
  trackJusGPTLimitWarningShown,
  trackJusGPTLimitReached,
} from "../helpers/trackJusGPTEvents";

// Disparado após upgrade de plano para a página do JusGPT (se montada) refazer o GET /usage
export const JUSGPT_QUOTA_REFETCH_EVENT = "jusgpt:quota-refetch";

// Limite a partir deste valor é virtualmente ilimitado: nenhuma UI de quota
// (badge, banner de bloqueio, modal) — o usuário não pode saber que foi barrado
const VIRTUALLY_UNLIMITED_THRESHOLD = 1000;

export const isJusGPTVirtuallyUnlimited = (limit) =>
  typeof limit === "number" && limit >= VIRTUALLY_UNLIMITED_THRESHOLD;

// Fail-open: sem dados ou erro de consulta => trata como ilimitado e não renderiza UI de quota
const UNLIMITED_QUOTA = {
  unlimited: true,
  blocked: false,
  nearLimit: false,
  used: null,
  limit: null,
  remaining: null,
  periodEnd: null,
  windowSource: null,
  plan: "",
};

// Timestamp do primeiro bloqueio, por usuário — usado para calcular dias_ate_upgrade.
// null no upgrade indica conversão sem ter batido o teto (via aviso de 90%).
const LIMIT_REACHED_AT_STORAGE_KEY = "jusgpt_limit_reached_at";

const limitReachedStorageKey = (userId) =>
  userId ? `${LIMIT_REACHED_AT_STORAGE_KEY}_${userId}` : LIMIT_REACHED_AT_STORAGE_KEY;

const persistJusGPTLimitReachedAt = (userId) => {
  try {
    const key = limitReachedStorageKey(userId);
    if (!localStorage.getItem(key)) {
      localStorage.setItem(key, String(Date.now()));
    }
  } catch (error) {
    // localStorage indisponível não pode quebrar o fluxo
  }
};

// Lê o timestamp do primeiro bloqueio, limpa a chave e devolve a diferença em dias
// inteiros. null se o usuário nunca bloqueou (converteu pelo aviso de 90%).
export const consumeJusGPTDaysUntilUpgrade = (userId) => {
  try {
    const key = limitReachedStorageKey(userId);
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    localStorage.removeItem(key);

    const timestamp = Number(raw);
    if (!Number.isFinite(timestamp)) return null;

    return Math.max(0, Math.floor((Date.now() - timestamp) / (24 * 60 * 60 * 1000)));
  } catch (error) {
    return null;
  }
};

/**
 * Chamar no callback de sucesso do checkout de upgrade: invalida o cache de quota
 * no backend (TTL 60s) e avisa a página do JusGPT para refazer o GET /usage.
 * Sem isso, o usuário paga e continua bloqueado por até 60 segundos.
 */
export const notifyJusGPTUpgradeSuccess = async (authToken) => {
  try {
    if (authToken) {
      await invalidateJusGPTUsage(`Bearer ${authToken}`);
    }
  } catch (error) {
    console.error("Erro ao invalidar cache de quota do JusGPT:", error);
  } finally {
    window.dispatchEvent(new Event(JUSGPT_QUOTA_REFETCH_EVENT));
  }
};

export function useJusGPTQuota(authToken, { disabled = false, userId } = {}) {
  const [quota, setQuota] = useState(UNLIMITED_QUOTA);

  // Dedup "uma vez por sessão" dos eventos de funil — em memória, de propósito
  const warningTrackedRef = useRef(false);
  const limitReachedTrackedRef = useRef(false);
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  // Bloqueio detectado (WS ou load): persiste o timestamp do primeiro bloqueio
  // e reporta o evento uma única vez por sessão
  const registerLimitReached = useCallback(({ plan, limit, periodEnd }) => {
    persistJusGPTLimitReachedAt(userIdRef.current);

    if (limitReachedTrackedRef.current) return;
    limitReachedTrackedRef.current = true;

    trackJusGPTLimitReached({ plan, limit, periodEnd });
  }, []);

  const fetchUsage = useCallback(async () => {
    if (disabled || !authToken) return;

    try {
      const { data } = await getJusGPTUsage(`Bearer ${authToken}`);

      if (!data) {
        setQuota(UNLIMITED_QUOTA);
        return;
      }

      const limit = data.limit ?? null;
      const virtuallyUnlimited = isJusGPTVirtuallyUnlimited(limit);
      const blocked = Boolean(data.blocked) && !virtuallyUnlimited;

      setQuota({
        unlimited: Boolean(data.unlimited) || virtuallyUnlimited,
        blocked,
        nearLimit: Boolean(data.nearLimit) && !virtuallyUnlimited,
        used: data.used ?? null,
        limit,
        remaining: data.remaining ?? null,
        periodEnd: data.periodEnd ?? null,
        windowSource: data.windowSource ?? null,
        plan: data.plan ?? "",
      });

      // Caminho do load: usuário já chegou bloqueado
      if (blocked && !data.unlimited) {
        registerLimitReached({
          plan: data.plan ?? "",
          limit,
          periodEnd: data.periodEnd ?? null,
        });
      }
    } catch (error) {
      // Falha de rede nunca bloqueia a UI
      console.error("Erro ao consultar quota do JusGPT:", error);
      setQuota(UNLIMITED_QUOTA);
    }
  }, [authToken, disabled, registerLimitReached]);

  // Badge de 90% ficou visível: reporta a transição de nearLimit, uma vez por sessão
  useEffect(() => {
    if (disabled || warningTrackedRef.current) return;
    if (quota.unlimited || !quota.nearLimit) return;

    warningTrackedRef.current = true;
    trackJusGPTLimitWarningShown({
      plan: quota.plan,
      used: quota.used,
      limit: quota.limit,
    });
  }, [quota, disabled]);

  // GET /usage no load da página, ao recuperar o foco da aba e após upgrade de plano.
  // O endpoint é a fonte da verdade: o evento WS é otimista e o refetch no foco corrige.
  useEffect(() => {
    if (disabled) return;

    fetchUsage();

    const handleRefetch = () => fetchUsage();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") fetchUsage();
    };

    window.addEventListener("focus", handleRefetch);
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener(JUSGPT_QUOTA_REFETCH_EVENT, handleRefetch);

    return () => {
      window.removeEventListener("focus", handleRefetch);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener(JUSGPT_QUOTA_REFETCH_EVENT, handleRefetch);
    };
  }, [fetchUsage, disabled]);

  // Evento WS jusgpt_usage_updated: atualiza o badge sem refetch.
  // O evento só é emitido para usuários com limite, então unlimited vira false.
  const applyUsageUpdate = useCallback((payload = {}) => {
    setQuota((prev) => {
      const used = typeof payload.used === "number" ? payload.used : prev.used;
      const limit = typeof payload.limit === "number" ? payload.limit : prev.limit;
      const hasNumbers = typeof used === "number" && typeof limit === "number";
      const virtuallyUnlimited = isJusGPTVirtuallyUnlimited(limit);

      return {
        ...prev,
        unlimited: virtuallyUnlimited,
        used,
        limit,
        remaining: hasNumbers ? Math.max(limit - used, 0) : prev.remaining,
        nearLimit: Boolean(payload.near_limit) && !virtuallyUnlimited,
        blocked: !virtuallyUnlimited && (prev.blocked || (hasNumbers && used >= limit)),
      };
    });
  }, []);

  // Evento WS jusgpt_limit_reached: envio barrado no teto
  // Limite virtualmente ilimitado nunca entra em estado bloqueado:
  // o usuário não pode saber que o envio foi barrado
  const applyLimitReached = useCallback((payload = {}) => {
    // Tracking usa o payload do WS (period_end fresco), não o estado do hook —
    // evita dessincronia se o bloqueio chegar antes do refetch
    if (!isJusGPTVirtuallyUnlimited(payload.limit)) {
      registerLimitReached({
        plan: payload.plan ?? "",
        limit: typeof payload.limit === "number" ? payload.limit : null,
        periodEnd: payload.period_end ?? null,
      });
    }

    setQuota((prev) => {
      const limit = typeof payload.limit === "number" ? payload.limit : prev.limit;
      const virtuallyUnlimited = isJusGPTVirtuallyUnlimited(limit);

      return {
        ...prev,
        unlimited: virtuallyUnlimited,
        blocked: !virtuallyUnlimited,
        nearLimit: !virtuallyUnlimited,
        used: typeof payload.used === "number" ? payload.used : prev.used,
        limit,
        remaining: virtuallyUnlimited ? prev.remaining : 0,
        periodEnd: payload.period_end ?? prev.periodEnd,
        plan: payload.plan ?? prev.plan,
      };
    });
  }, [registerLimitReached]);

  return { quota, refetchUsage: fetchUsage, applyUsageUpdate, applyLimitReached };
}
